import { BitkitEmptyState, IconBuild } from '@bitrise/bitkit-v2';
import { isAxiosError } from 'axios';
import { useDebounceValue } from 'usehooks-ts';

import ManualApprovalBuildList from '@/pages/App/AppDetails/components/BuildsSection/ManualApprovalBuildList/ManualApprovalBuildList';
import ScheduledBuilds from '@/pages/App/AppDetails/components/ScheduledBuilds/ScheduledBuilds';
import { ScheduledBuild } from '@/pages/App/AppDetails/components/ScheduledBuilds/scheduledBuilds.types';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import { SettingsPageAbilities } from '@/pages/App/AppSettings/types/SettingsPageAbilities';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import ArchivedBuildsSection from '@/pages/BuildList/ArchivedBuildsSection';

import BuildList from './BuildList/BuildList';
import useBuilds from './BuildList/useBuilds';

type BuildSectionProps = {
  app: AppForAppPage;
  abilities: SettingsPageAbilities;
  filters: BuildListFilters;
  isViewedAsPublicPage: boolean;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
};

const transientClientErrorStatuses = [408, 429];

const isPermanentClientError = (error: Error | null): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500 && !transientClientErrorStatuses.includes(status);
};

const BuildsSection = ({
  app,
  abilities,
  filters,
  isViewedAsPublicPage,
  onUpdateScheduledBuild,
}: BuildSectionProps) => {
  const [debouncedFilters] = useDebounceValue(filters, 50);

  const {
    builds,
    count,
    hasNextPage,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
    isFetchingNextPageError,
    isFetching,
    isError,
    isSuccess,
  } = useBuilds(
    {
      appSlug: app.slug,
      filters: debouncedFilters,
      isOnHold: false,
      limit: 25,
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      // Polling can't clear a permanent client error, so wait for a manual retry instead of hitting it every 15s forever
      // (SSW-3192).
      // A failed "Load more" doesn't count: the failed page never enters the cache, so the next poll refetches only
      // the pages that did load and still picks up new builds.
      refetchInterval: (query) =>
        isPermanentClientError(query.state.error) && !query.state.fetchMeta?.fetchMore ? false : 15000,
      requestConfig: {
        publicAccess: isViewedAsPublicPage,
      },
    },
  );

  return (
    <>
      {!isViewedAsPublicPage && app.isManualApprovalEnabled && (
        <ManualApprovalBuildList app={app} abilities={abilities} />
      )}
      {!isViewedAsPublicPage && <ScheduledBuilds app={app} onUpdateScheduledBuild={onUpdateScheduledBuild} />}
      {!app.hasAnyBuild && (
        <BitkitEmptyState
          bodyText="Set up your Workflows or use the default ones to start running builds."
          icon={IconBuild}
          headingText="Your builds will appear here"
        />
      )}
      {app.hasAnyBuild && (
        <>
          <BuildList
            filters={filters}
            app={app}
            canRunBuild={abilities.canRunBuilds}
            builds={builds}
            count={count}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            refetch={refetch}
            isFetchingNextPage={isFetchingNextPage}
            isFetchingNextPageError={isFetchingNextPageError}
            isFetching={isFetching}
            isError={isError}
            isSuccess={isSuccess}
          />
          <ArchivedBuildsSection appSlug={app.slug} />
        </>
      )}
    </>
  );
};

export default BuildsSection;
